import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { screenService } from "@/services/screen/screen.service";
import { toast } from "sonner";
import { Navigate, useNavigate, useParams } from "react-router";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wifi, WifiOff, Monitor, Building, Search, XCircle } from "lucide-react";
import { useWebSocket } from "@/hooks/useWebSocket";
import { ScreenEvents } from "@exibra/shared";
import { Separator } from "@/components/ui/separator";
import { useTenants } from "@/api/tenant.api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useNextSerialNumber, usePairScreen } from "@/api/screen.api";
import { cn } from "@/lib/utils";
import { typedZodResolver } from "@/lib/typed-zod-resolver";

type ErrorMessage = "screens.pairing.errors.nameRequired" | "screens.pairing.errors.noTenant";

const screenPairingSchema = z.object({
    name: z.string().min(1, { message: "screens.pairing.errors.nameRequired" as ErrorMessage }),
    tenantId: z.string().min(1, { message: "screens.pairing.errors.noTenant" as ErrorMessage }),
});

type ScreenPairingFormData = z.infer<typeof screenPairingSchema>;

export const ScreenPairing = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();
    const [isOnline, setIsOnline] = useState<boolean | null>(null);
    const [isLocating, setIsLocating] = useState(false);
    const { getSocket } = useWebSocket();
    const { data: tenants, isLoading: isLoadingTenants } = useTenants();
    const { selectedTenant } = useTenantStore();
    const { mutateAsync: pairScreen } = usePairScreen();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, touchedFields },
        setValue,
        watch,
    } = useForm<ScreenPairingFormData>({
        resolver: typedZodResolver<ScreenPairingFormData>(screenPairingSchema),
        defaultValues: {
            tenantId: selectedTenant ? String(selectedTenant.id) : undefined,
        },
    });

    const selectedTenantId = watch("tenantId");

    const { data: nextSerialNumber } = useNextSerialNumber(Number(selectedTenantId));

    // Check if the screen is online when the component mounts
    useEffect(() => {
        if (!uuid) {
            setIsOnline(false);
            return;
        }

        const checkStatus = async () => {
            try {
                const [isValid, isLocating] = await Promise.all([
                    screenService.validateSerialNumber(uuid),
                    screenService.isScreenLocating(uuid),
                ]);
                setIsOnline(isValid);
                setIsLocating(isLocating);
            } catch (error) {
                console.error("Error checking screen status:", error);
                setIsOnline(false);
                setIsLocating(false);
            }
        };

        checkStatus();
    }, [uuid]);

    // Subscribe to WebSocket events for real-time screen status updates
    useEffect(() => {
        if (!uuid) return;

        const socket = getSocket();
        if (!socket) return;

        const handleScreenRegistered = (data: { registrationNumber: string }) => {
            if (data.registrationNumber === uuid) {
                setIsOnline(true);
            }
        };

        const handleScreenDisconnected = () => {
            // Since we don't have the screen ID here, we'll need to revalidate
            screenService.validateSerialNumber(uuid).then(setIsOnline);
        };

        socket.on(ScreenEvents.SCREEN_REGISTERED, handleScreenRegistered);
        socket.on(ScreenEvents.SCREEN_DISCONNECTED, handleScreenDisconnected);

        return () => {
            socket.off(ScreenEvents.SCREEN_REGISTERED, handleScreenRegistered);
            socket.off(ScreenEvents.SCREEN_DISCONNECTED, handleScreenDisconnected);
        };
    }, [uuid, getSocket]);

    const onSubmit = async (data: ScreenPairingFormData) => {
        if (!uuid) {
            toast.error(t("screens.pairing.errors.noUuid"));
            return;
        }

        try {
            const screen = await pairScreen({
                uuid,
                tenantId: Number(data.tenantId),
                name: data.name,
                serialNumber: nextSerialNumber!,
            });
            toast.success(t("screens.pairing.success"));
            navigate(`/tenant/${screen.tenantId}/screens/${screen.id}`);
        } catch (error) {
            console.error("Error pairing screen:", error);
            toast.error(t("screens.pairing.errors.pairingFailed"));
        }
    };

    const handleLocate = async () => {
        if (!uuid) return;

        try {
            setIsLocating(!isLocating);
            await screenService.locateScreen(uuid);
            toast.success(
                isLocating ? t("screens.pairing.locateStop") : t("screens.pairing.locateSuccess"),
            );
        } catch (error) {
            console.error("Error locating screen:", error);
            setIsLocating(false);
            toast.error(t("screens.pairing.locateError"));
        }
    };

    if (!uuid) {
        return <Navigate to="/" />;
    }

    return (
        <div className="container mx-auto py-8">
            <Card className="max-w-md mx-auto shadow-lg">
                <CardHeader className="space-y-4">
                    <div className="flex items-center space-x-2">
                        <Monitor className="h-5 w-5 text-primary" />
                        <CardTitle>{t("screens.pairing.title")}</CardTitle>
                    </div>
                    <CardDescription>{t("screens.pairing.description")}</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="space-y-6">
                            {/* Screen Identification Section */}
                            <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-muted-foreground">
                                        {t("screens.pairing.uuid")}
                                    </h3>

                                    {isOnline === null ? (
                                        <Badge
                                            variant="outline"
                                            className="min-w-20 justify-center"
                                        >
                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                            {t("screens.pairing.status.checking")}
                                        </Badge>
                                    ) : isOnline ? (
                                        <Badge
                                            variant="default"
                                            className="min-w-20 justify-center bg-green-600 hover:bg-green-700"
                                        >
                                            <Wifi className="mr-1.5 h-3 w-3" />
                                            {t("screens.pairing.status.online")}
                                        </Badge>
                                    ) : (
                                        <Badge
                                            variant="destructive"
                                            className="min-w-20 justify-center"
                                        >
                                            <WifiOff className="mr-1.5 h-3 w-3" />
                                            {t("screens.pairing.status.offline")}
                                        </Badge>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                        <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm">
                                            {uuid}
                                        </code>
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    variant={isLocating ? "destructive" : "outline"}
                                    size="sm"
                                    onClick={handleLocate}
                                    disabled={!isOnline}
                                    className={cn(
                                        `transition-colors ${isLocating ? "animate-pulse" : ""}`,
                                        "w-full",
                                    )}
                                >
                                    {isLocating ? (
                                        <>
                                            <XCircle className="mr-2 h-4 w-4" />
                                            {t("screens.pairing.locateStop")}
                                        </>
                                    ) : (
                                        <>
                                            <Search className="mr-2 h-4 w-4" />
                                            {t("screens.pairing.locate")}
                                        </>
                                    )}
                                </Button>
                            </div>

                            <Separator />

                            {/* Organization Selection */}
                            <div className="space-y-4">
                                <div className="flex flex-col space-y-2">
                                    <label className="text-sm font-medium">
                                        {t("screens.pairing.tenant")}
                                    </label>
                                    <Select
                                        onValueChange={(value) => setValue("tenantId", value)}
                                        disabled={isLoadingTenants}
                                        defaultValue={
                                            selectedTenant ? String(selectedTenant.id) : undefined
                                        }
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={
                                                    isLoadingTenants
                                                        ? t("screens.pairing.loadingTenants")
                                                        : t("screens.pairing.selectTenant")
                                                }
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tenants?.map((tenant) => (
                                                <SelectItem
                                                    key={tenant.id}
                                                    value={String(tenant.id)}
                                                >
                                                    <div className="flex items-center">
                                                        <Building className="mr-2 h-4 w-4" />
                                                        {tenant.name}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.tenantId && (
                                        <p className="text-sm text-destructive">
                                            {t(errors.tenantId.message as ErrorMessage)}
                                        </p>
                                    )}
                                </div>

                                {selectedTenantId && nextSerialNumber && (
                                    <div className="rounded-md bg-muted p-3">
                                        <p className="text-sm text-muted-foreground">
                                            {t("screens.pairing.nextSerialNumber")}
                                        </p>
                                        <code className="text-sm">{nextSerialNumber}</code>
                                    </div>
                                )}
                            </div>

                            <Separator />

                            {/* Screen Configuration Section */}
                            <div className="space-y-4">
                                <FormField
                                    label={t("screens.pairing.name")}
                                    error={
                                        errors.name?.message
                                            ? t(errors.name.message as ErrorMessage)
                                            : undefined
                                    }
                                    touched={touchedFields.name}
                                    registration={register("name")}
                                    required
                                />
                            </div>
                        </div>

                        <CardFooter className="flex justify-end px-0 pb-0 pt-2">
                            <Button
                                type="submit"
                                disabled={isSubmitting || !isOnline || !selectedTenantId}
                                className="w-32"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t("general.pairing")}
                                    </>
                                ) : (
                                    t("general.pair")
                                )}
                            </Button>
                        </CardFooter>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};
